import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { concatMap, distinctUntilChanged, takeUntil, windowTime } from 'rxjs/operators';

import { MessageType } from './message-type.enum';
import { Message } from './message.interface';

const iFrameHookedUpAlready = Symbol('iFrameHookedUpAlready');
/** Max number of attempts to initialize the communication with epo */
const maxInitializationAttempts = 30;
/**
 * Service to communicate with inner shell.
 * Utilizes MessageChannel to send and receive messages to the inner app
 */
@Injectable({
	providedIn: 'root',
})
export class IFrameCommService implements OnDestroy {
	// TODO: add a mutationObserver to the iframe, so we can clean up, and don't leak memory.

	private destroy$ = new Subject<void>();

	/**
	 * internal subject tht lives for the lifetime of the app
	 */
	private incomingMessages = new Subject<Message>();
	private inChannel: MessageChannel;
	private outChannel: MessageChannel;
	private isConnected = false;
	private initializationAttempts = 0;
	private navigationMessages = new Subject<string>();

	/**
	 * Observable<Message>. lives for the duration of the app. it will emit
	 * all messages that come from the inner app.
	 */
	public incomingMessages$ = this.incomingMessages.asObservable();

	constructor() {}

	sendNavigationMessage(url: string) {
		this.navigationMessages.next(url);
	}
	/**
	 * Sends an message to the inner app.
	 * this is also used to return information. (the button clicked in a swal for example, or the route to navigate to)
	 * @param message
	 */
	send(message: Message) {
		if (this.outChannel) {
			// only send if the out-channel is indeed up.
			this.outChannel.port1.postMessage(message);
		}
	}

	_emitForTestsOnly(msg: Message) {
		this.incomingMessages.next(msg);
	}

	/**
	 * Cleanup. closes the connection to the inner app.     *
	 */
	destroy() {
		this.isConnected = false;
		if (this.inChannel) {
			this.inChannel.port1.close();
			this.inChannel.port2.close();
		}
		if (this.outChannel) {
			this.outChannel.port1.close();
			this.outChannel.port2.close();
		}
		this.inChannel = undefined;
		this.outChannel = undefined;
	}

	/**
	 * setter to set the connection state to true.
	 */
	private connect() {
		this.isConnected = true;
	}

	/**
	 * Setup the connection to the inner app.
	 * Returns a promise when done.
	 * When a connection  already open, it will reuse that.
	 * the promise will fail when the iframe didn't load ok. or when the connection is already made.
	 * the failed promise will hold the cause of the failure.
	 * @param  iFrame that continues the inner app.
	 */
	setupConnection(iFrame: HTMLIFrameElement) {
		this.initializationAttempts = 0;

		return new Promise<void>((resolve, reject) => {
			const hookUp = () => {
				if (iFrame.contentDocument.location.href.toLocaleLowerCase() === 'about:blank') {
					return reject('notFound ');
				}
				/**
				 * If this is called, the reason is bcs the iframe has reinitialized somehow.
				 * so we need to hook it up once more, otherwise the RPC will not work.
				 * this can happen if a route outside the main app is called (like logoff for example)
				 */

				// When the iframe is done loading, hook up the communication
				this.inChannel = new MessageChannel();
				this.outChannel = new MessageChannel();

				// only hook up when the content is loaded/
				this.inChannel.port1.onmessage = ev => this.incomingMessages.next(ev.data);

				iFrame.contentWindow.addEventListener('message', function (ev) {});
				(<any>window).myIframe1 = iFrame;
				iFrame.contentWindow.postMessage('init channel', location.origin, [this.inChannel.port2, this.outChannel.port2]);
				this.connect();
				iFrame[iFrameHookedUpAlready] = true;
				resolve();
				this.listenToNavigationChanges();
			};

			// Waits for epo to subscribe on the message event on the window
			const hookUpWhenPossible = () => {
				// We stop trying after x attempts
				if (this.initializationAttempts > maxInitializationAttempts) {
					return;
				}

				// Retry when EPO is not ready yet
				if ((<any>window).isEpoReady == null) {
					this.initializationAttempts++;
					setTimeout(hookUpWhenPossible, 100);
					return;
				}

				hookUp();
			};

			if (!iFrame[iFrameHookedUpAlready]) {
				iFrame.addEventListener('load', hookUpWhenPossible);
			} else {
				return Promise.resolve();
			}
		});
	}

	listenToNavigationChanges() {
		this.navigationMessages
			.pipe(
				windowTime(1000),
				concatMap(obs => obs.pipe(distinctUntilChanged())),
				takeUntil(this.destroy$)
			)
			.subscribe(url => {
				this.send({
					type: MessageType.navTo,
					url,
				});
			});
	}

	/**
	 * Cleanup
	 */
	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.destroy();
	}
}
